import { useId, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDiscardUnavailableData } from "@softmax/web-features/data";
import { programDetailOptions } from "./program-detail.queries";
import { plainName } from "../../lib/program-names";
import { ProgramFacts } from "./program-facts";
import s from "../onepager.module.css";

export type ProgramViewer = {
  readonly coworld: string;
  readonly leagueId: string;
  /** Sign-in returns to this league's Play page. */
  readonly playPath: string;
  readonly divisionId: string | null;
  readonly corpus: string;
};

type ProgramRowProps = {
  readonly label: string;
  readonly meta: string;
  readonly id?: string | null;
  readonly by?: string | null;
  readonly starter?: boolean;
  readonly viewer: ProgramViewer;
};

export function ProgramRow({
  label,
  meta,
  id = null,
  by = null,
  starter = false,
  viewer,
}: ProgramRowProps) {
  const [open, setOpen] = useState(false);
  const options = programDetailOptions({
    leagueId: viewer.leagueId,
    corpus: viewer.corpus,
    divisionId: viewer.divisionId,
    label,
    starter,
    id,
    by,
  });
  const read = useQuery({ ...options, enabled: open });
  const unavailable = useDiscardUnavailableData({
    queryKey: options.queryKey,
    error: read.error,
  });
  const detail = unavailable ? undefined : read.data;
  const panelId = useId();
  return (
    <li className={s.programItem} data-open={open || undefined}>
      <button
        type="button"
        className={s.programButton}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
      >
        <code className={s.programLabel}>{plainName(label)}</code>
        <span className={s.programMeta}>
          {meta}
          <svg
            className={s.chevron}
            viewBox="0 0 12 12"
            width="12"
            height="12"
            aria-hidden="true"
          >
            <path d="M3 4.5 6 7.5 9 4.5" />
          </svg>
        </span>
      </button>
      <div id={panelId} className={s.programDetail} hidden={!open}>
        {detail ? (
          <>
            {read.isError ? (
              <output style={{ display: "block" }}>
                Could not refresh this program.{" "}
                <button type="button" onClick={() => void read.refetch()}>
                  Retry
                </button>
              </output>
            ) : null}
            {detail.description ? (
              <p className={s.programText}>{detail.description}</p>
            ) : null}
            <ProgramFacts detail={detail} coworld={viewer.coworld} />
          </>
        ) : (
          <output className={s.programNote} style={{ display: "block" }}>
            {read.isError ? "Couldn't load this program." : "Loading…"}
            {read.isError ? (
              <button type="button" onClick={() => void read.refetch()}>
                Retry
              </button>
            ) : null}
          </output>
        )}
      </div>
    </li>
  );
}
